import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { RefreshCw, Truck } from 'lucide-react';

function ProgressCard({ title, icon, total, done, color = 'green', extra }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="card" style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 28 }}>{icon}</span>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--green-900)', fontSize: 15 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{total} ekor terdaftar</div>
        </div>
      </div>
      <div className="progress-bar" style={{ marginBottom: 6 }}>
        <div className="progress-fill" style={{ width: `${pct}%`, background: color === 'amber' ? 'linear-gradient(90deg,#d97706,#fbbf24)' : undefined }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
        <span>{done} selesai</span><span style={{ fontWeight: 700, color: color === 'amber' ? '#d97706' : 'var(--green-700)' }}>{pct}%</span>
      </div>
      {extra}
    </div>
  );
}

export default function MonitoringPage() {
  const [stats, setStats] = useState({ sapi: { total: 0, sembelih: 0, packing: 0, distribusi: 0 }, kambing: { total: 0, sembelih: 0, packing: 0, distribusi: 0 } });
  const [couriers, setCouriers] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    // Listen to animals collection
    const unsub1 = onSnapshot(query(collection(db, 'animals')), (snap) => {
      const sapi = { total: 0, sembelih: 0, packing: 0, distribusi: 0 };
      const kambing = { total: 0, sembelih: 0, packing: 0, distribusi: 0 };
      snap.forEach(doc => {
        const d = doc.data();
        const target = d.type === 'sapi' ? sapi : kambing;
        target.total++;
        if (['sembelih', 'packing', 'distribusi', 'selesai'].includes(d.status)) target.sembelih++;
        if (['packing', 'distribusi', 'selesai'].includes(d.status)) target.packing++;
        if (['distribusi', 'selesai'].includes(d.status)) target.distribusi++;
      });
      setStats({ sapi, kambing });
      setLastUpdate(new Date());
    });

    const unsub2 = onSnapshot(query(collection(db, 'couriers')), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.status === 'active');
      setCouriers(list);
    });

    return () => { unsub1(); unsub2(); };
  }, []);

  const { sapi, kambing } = stats;
  const totalHewan = sapi.total + kambing.total;
  const totalSembelih = sapi.sembelih + kambing.sembelih;
  const totalPacking = sapi.packing + kambing.packing;
  const totalDistribusi = sapi.distribusi + kambing.distribusi;

  return (
    <div style={{ paddingTop: 80, minHeight: '100vh', background: '#f4fbf6' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem 4rem' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--green-100)', borderRadius: 999, padding: '5px 14px', marginBottom: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            <span style={{ color: 'var(--green-700)', fontSize: 13, fontWeight: 600 }}>Live Monitoring</span>
            {lastUpdate && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>· {lastUpdate.toLocaleTimeString('id-ID')}</span>}
          </div>
          <h1 style={{ fontSize: '2rem', color: 'var(--green-900)' }}>Monitoring Proses Qurban</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>Data diperbarui secara otomatis. Tidak perlu refresh halaman.</p>
        </div>

        {/* Summary stats */}
        <div className="stats-grid" style={{ marginBottom: '2rem' }}>
          {[
            { label: 'Total Hewan', value: totalHewan, sub: 'Sapi & Kambing', color: '#166534' },
            { label: 'Disembelih', value: totalSembelih, sub: `${totalHewan > 0 ? Math.round(totalSembelih/totalHewan*100) : 0}% selesai`, color: '#dc2626' },
            { label: 'Dipacking', value: totalPacking, sub: `${totalHewan > 0 ? Math.round(totalPacking/totalHewan*100) : 0}% selesai`, color: '#d97706' },
            { label: 'Didistribusi', value: totalDistribusi, sub: `${totalHewan > 0 ? Math.round(totalDistribusi/totalHewan*100) : 0}% selesai`, color: '#0369a1' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Main progress cards */}
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--green-800)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 13 }}>PROGRES PER HEWAN</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
          <ProgressCard title="Sapi" icon="🐄" total={sapi.total} done={sapi.sembelih}
            extra={
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Penyembelihan', val: sapi.sembelih },
                  { label: 'Packing', val: sapi.packing },
                  { label: 'Distribusi', val: sapi.distribusi },
                  { label: 'Total Sapi', val: sapi.total },
                ].map((m, i) => (
                  <div key={i} style={{ background: 'var(--green-50)', borderRadius: 10, padding: '8px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green-700)' }}>{m.val}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.label}</div>
                  </div>
                ))}
              </div>
            }
          />
          <ProgressCard title="Kambing" icon="🐏" total={kambing.total} done={kambing.sembelih} color="amber"
            extra={
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Penyembelihan', val: kambing.sembelih },
                  { label: 'Packing', val: kambing.packing },
                  { label: 'Distribusi', val: kambing.distribusi },
                  { label: 'Total Kambing', val: kambing.total },
                ].map((m, i) => (
                  <div key={i} style={{ background: '#fef3c7', borderRadius: 10, padding: '8px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#d97706' }}>{m.val}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.label}</div>
                  </div>
                ))}
              </div>
            }
          />
        </div>

        {/* Progress stages detailed */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)', marginBottom: 20 }}>Progres Keseluruhan Proses</h3>
          {[
            { label: '🔪 Penyembelihan', done: totalSembelih, total: totalHewan, color: '#dc2626' },
            { label: '📦 Packing Daging', done: totalPacking, total: totalHewan, color: '#d97706' },
            { label: '🚚 Distribusi Daging', done: totalDistribusi, total: totalHewan, color: '#0369a1' },
          ].map((stage, i) => {
            const pct = stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 0;
            return (
              <div key={i} style={{ marginBottom: i < 2 ? 16 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{stage.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: stage.color }}>{stage.done}/{stage.total} ({pct}%)</span>
                </div>
                <div className="progress-bar" style={{ height: 14 }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: stage.color, borderRadius: 999, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Active couriers */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)' }}>🚚 Kurir Aktif Saat Ini</h3>
            <span className="badge badge-green">{couriers.length} bertugas</span>
          </div>
          {couriers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              <Truck size={32} style={{ margin: '0 auto 8px', opacity: 0.4, display: 'block' }} />
              Belum ada kurir aktif saat ini
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {couriers.map(c => (
                <Link key={c.id} to={`/tracking/${c.id}`} style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'var(--green-50)', border: '1.5px solid var(--green-200)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--green-500)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--green-200)'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--green-700)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                        {c.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--green-900)' }}>{c.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.vehicle || 'Motor'} · {c.phone}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--green-700)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                      Tap untuk lihat lokasi live
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
  );
}
